import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  AlertColor,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  Divider
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import EventIcon from '@mui/icons-material/Event';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import HomeIcon from '@mui/icons-material/Home';
import SchoolIcon from '@mui/icons-material/School';
import { surgicalPhasesService } from '../../../services/surgicalPhasesService';
import { useUserData } from '../../../hooks/useUserData';

interface SurgicalProcedureData {
  id: string;
  procedureType: string;
  specialty: string;
  status: string;
  orderDate: Date;
  estimatedDuration?: number;
  requiresConsent?: boolean;
  specialInstructions?: string;
  orderingProvider?: string;
  priority?: string;
}

interface Patient {
  id: string;
  name: string;
  mrn?: string;
}

interface SurgicalPostOpPhaseProps {
  surgicalProcedure: SurgicalProcedureData;
  patient: Patient;
  encounterId?: string;
}

interface PostOpOrder {
  id: string;
  type: string;
  description: string;
  dosage?: string;
  frequency?: string;
  duration?: string;
  provider: string;
}

interface DischargeInstruction {
  category: string;
  instruction: string;
  priority: 'High' | 'Medium' | 'Low';
}

interface Prescription {
  id: string;
  medication: string;
  dosage: string;
  quantity: string;
  frequency: string;
  duration: string;
  refills: number;
  instructions: string;
}

interface HomeCareItem {
  category: string;
  description: string;
  frequency: string;
  duration: string;
}

interface EducationTopic {
  topic: string;
  completed: boolean;
  provider: string;
}

const SurgicalPostOpPhase: React.FC<SurgicalPostOpPhaseProps> = ({ 
  surgicalProcedure, 
  patient, 
  encounterId 
}) => {
  const { user } = useUserData();
  
  // State Management
  const [checklistStatus, setChecklistStatus] = useState({
    postOpOrders: false,
    dischargeInstructions: false,
    followUpScheduled: false,
    prescriptions: false,
    homeCarePlanning: false,
    patientEducation: false
  });

  const [openDialog, setOpenDialog] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Post-Op Orders State
  const [postOpOrders, setPostOpOrders] = useState<PostOpOrder[]>([]);
  const [orderForm, setOrderForm] = useState({
    type: '',
    description: '',
    dosage: '',
    frequency: '',
    duration: ''
  });

  // Discharge Instructions State
  const [dischargeInstructions, setDischargeInstructions] = useState<DischargeInstruction[]>([]);
  const [instructionForm, setInstructionForm] = useState({
    category: '',
    instruction: '',
    priority: 'Medium' as 'High' | 'Medium' | 'Low'
  });

  // Follow-up Appointment State
  const [followUpForm, setFollowUpForm] = useState({
    appointmentType: '',
    provider: '',
    timeframe: '',
    notes: ''
  });

  // Prescriptions State
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [prescriptionForm, setPrescriptionForm] = useState({
    medication: '',
    dosage: '',
    quantity: '',
    frequency: '',
    duration: '',
    refills: 0,
    instructions: ''
  });

  // Home Care Planning State
  const [homeCareItems, setHomeCareItems] = useState<HomeCareItem[]>([]);
  const [homeCareForm, setHomeCareForm] = useState({
    category: '',
    description: '',
    frequency: '',
    duration: ''
  });

  // Patient Education State
  const [educationTopics, setEducationTopics] = useState<EducationTopic[]>([]);
  const [educationForm, setEducationForm] = useState({
    topic: '',
    completed: false
  });

  const showSnackbar = (message: string, severity: AlertColor) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Handler Functions
  const handleReviewOrders = () => {
    setOpenDialog('postOpOrders');
  };

  const handleGenerateInstructions = () => {
    setOpenDialog('dischargeInstructions');
  };

  const handleScheduleFollowUp = () => {
    setOpenDialog('followUp');
  };

  const handleWritePrescriptions = () => {
    setOpenDialog('prescriptions');
  };

  const handleDocumentPlan = () => {
    setOpenDialog('homeCarePlanning');
  };

  const handleProvideEducation = () => {
    setOpenDialog('patientEducation');
  };

  const handleAddOrder = async () => {
    if (!orderForm.type || !orderForm.description) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    setLoading(true);
    try {
      const newOrder: PostOpOrder = {
        id: Date.now().toString(),
        type: orderForm.type,
        description: orderForm.description,
        dosage: orderForm.dosage,
        frequency: orderForm.frequency,
        duration: orderForm.duration,
        provider: user?.name || 'Unknown'
      };

      setPostOpOrders(prev => [...prev, newOrder]);
      
      // Note: Orders will be saved when completing the entire orders section

      setOrderForm({ type: '', description: '', dosage: '', frequency: '', duration: '' });
      showSnackbar('Post-op order added successfully', 'success');
    } catch (error) {
      console.error('Error adding post-op order:', error);
      showSnackbar('Failed to add post-op order', 'error');
    }
    setLoading(false);
  };

  const handleCompleteOrders = async () => {
    if (postOpOrders.length === 0) {
      showSnackbar('Please add at least one post-op order', 'error');
      return;
    }

    setLoading(true);
    try {
      // Prepare orders data
      const ordersData = {
        encounterId,
        patientId: patient.id,
        surgicalProcedureId: surgicalProcedure.id,
        orders: postOpOrders
      };

      // Use service to create post-op orders
      await surgicalPhasesService.createPostOpOrders(ordersData);

      setChecklistStatus(prev => ({ ...prev, postOpOrders: true }));
      setOpenDialog(null);
      showSnackbar('Post-op orders completed successfully', 'success');
    } catch (error) {
      console.error('Error completing post-op orders:', error);
      showSnackbar('Failed to complete post-op orders', 'error');
    }
    setLoading(false);
  };

  const handleAddInstruction = async () => {
    if (!instructionForm.category || !instructionForm.instruction) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    const newInstruction: DischargeInstruction = {
      category: instructionForm.category,
      instruction: instructionForm.instruction,
      priority: instructionForm.priority
    };

    setDischargeInstructions(prev => [...prev, newInstruction]);
    setInstructionForm({ category: '', instruction: '', priority: 'Medium' });
    showSnackbar('Discharge instruction added', 'success');
  };

  const handleCompleteInstructions = async () => {
    if (dischargeInstructions.length === 0) {
      showSnackbar('Please add at least one discharge instruction', 'error');
      return;
    }

    setLoading(true);
    try {
      // Prepare instructions data
      const instructionsData = {
        encounterId,
        patientId: patient.id,
        surgicalProcedureId: surgicalProcedure.id,
        instructions: dischargeInstructions
      };

      // Use service to create discharge instructions
      await surgicalPhasesService.createDischargeInstructions(instructionsData);

      setChecklistStatus(prev => ({ ...prev, dischargeInstructions: true }));
      setOpenDialog(null);
      showSnackbar('Discharge instructions generated successfully', 'success');
    } catch (error) {
      console.error('Error generating discharge instructions:', error);
      showSnackbar('Failed to generate discharge instructions', 'error');
    }
    setLoading(false);
  };

  const handleCompleteFollowUp = async () => {
    if (!followUpForm.appointmentType || !followUpForm.provider || !followUpForm.timeframe) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    setLoading(true);
    try {
      // Prepare follow-up data
      const followUpData = {
        encounterId,
        patientId: patient.id,
        surgicalProcedureId: surgicalProcedure.id,
        appointmentType: followUpForm.appointmentType,
        provider: followUpForm.provider,
        timeframe: followUpForm.timeframe,
        notes: followUpForm.notes
      };

      // Use service to schedule follow-up
      await surgicalPhasesService.scheduleFollowUp(followUpData);

      setChecklistStatus(prev => ({ ...prev, followUpScheduled: true }));
      setOpenDialog(null);
      showSnackbar('Follow-up appointment scheduled successfully', 'success');
    } catch (error) {
      console.error('Error scheduling follow-up:', error);
      showSnackbar('Failed to schedule follow-up appointment', 'error');
    }
    setLoading(false);
  };

  const handleAddPrescription = () => {
    if (!prescriptionForm.medication || !prescriptionForm.dosage || !prescriptionForm.quantity) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    const newPrescription: Prescription = {
      id: Date.now().toString(),
      medication: prescriptionForm.medication,
      dosage: prescriptionForm.dosage,
      quantity: prescriptionForm.quantity,
      frequency: prescriptionForm.frequency,
      duration: prescriptionForm.duration,
      refills: prescriptionForm.refills,
      instructions: prescriptionForm.instructions
    };

    setPrescriptions(prev => [...prev, newPrescription]);
    setPrescriptionForm({
      medication: '',
      dosage: '',
      quantity: '',
      frequency: '',
      duration: '',
      refills: 0,
      instructions: ''
    });
    showSnackbar('Prescription added', 'success');
  };

  const handleCompletePrescriptions = async () => {
    if (prescriptions.length === 0) {
      showSnackbar('Please add at least one prescription', 'error');
      return;
    }

    setLoading(true);
    try {
      // Prepare prescriptions data
      const prescriptionsData = {
        encounterId,
        patientId: patient.id,
        surgicalProcedureId: surgicalProcedure.id,
        prescriptions: prescriptions
      };

      // Use service to create prescriptions
      await surgicalPhasesService.createPrescriptions(prescriptionsData);

      setChecklistStatus(prev => ({ ...prev, prescriptions: true }));
      setOpenDialog(null);
      showSnackbar('Prescriptions written successfully', 'success');
    } catch (error) {
      console.error('Error writing prescriptions:', error);
      showSnackbar('Failed to write prescriptions', 'error');
    }
    setLoading(false);
  };

  const handleAddHomeCareItem = () => {
    if (!homeCareForm.category || !homeCareForm.description) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    const newItem: HomeCareItem = {
      category: homeCareForm.category,
      description: homeCareForm.description,
      frequency: homeCareForm.frequency,
      duration: homeCareForm.duration
    };

    setHomeCareItems(prev => [...prev, newItem]);
    setHomeCareForm({ category: '', description: '', frequency: '', duration: '' });
    showSnackbar('Home care item added', 'success');
  };

  const handleCompleteHomeCare = async () => {
    if (homeCareItems.length === 0) {
      showSnackbar('Please add at least one home care item', 'error');
      return;
    }

    setLoading(true);
    try {
      // Prepare home care data
      const homeCareData = {
        encounterId,
        patientId: patient.id,
        surgicalProcedureId: surgicalProcedure.id,
        homeCareItems: homeCareItems
      };

      // Use service to create home care plan
      await surgicalPhasesService.createHomeCarePlan(homeCareData);

      setChecklistStatus(prev => ({ ...prev, homeCarePlanning: true }));
      setOpenDialog(null);
      showSnackbar('Home care plan documented successfully', 'success');
    } catch (error) {
      console.error('Error documenting home care plan:', error);
      showSnackbar('Failed to document home care plan', 'error');
    }
    setLoading(false);
  };

  const handleAddEducationTopic = () => {
    if (!educationForm.topic) {
      showSnackbar('Please enter an education topic', 'error');
      return;
    }

    const newTopic: EducationTopic = {
      topic: educationForm.topic,
      completed: educationForm.completed,
      provider: user?.name || 'Unknown'
    };

    setEducationTopics(prev => [...prev, newTopic]);
    setEducationForm({ topic: '', completed: false });
    showSnackbar('Education topic added', 'success');
  };

  const handleCompleteEducation = async () => {
    if (educationTopics.length === 0) {
      showSnackbar('Please add at least one education topic', 'error');
      return;
    }

    setLoading(true);
    try {
      // Prepare education data
      const educationData = {
        encounterId,
        patientId: patient.id,
        surgicalProcedureId: surgicalProcedure.id,
        educationTopics: educationTopics
      };

      // Use service to create patient education
      await surgicalPhasesService.createPatientEducation(educationData);

      setChecklistStatus(prev => ({ ...prev, patientEducation: true }));
      setOpenDialog(null);
      showSnackbar('Patient education completed successfully', 'success');
    } catch (error) {
      console.error('Error completing patient education:', error);
      showSnackbar('Failed to complete patient education', 'error');
    }
    setLoading(false);
  };

  const handleCompletePostOp = async () => {
    const completedItems = Object.values(checklistStatus).filter(Boolean).length;
    const totalItems = Object.keys(checklistStatus).length;

    if (completedItems < totalItems) {
      showSnackbar(`Please complete all checklist items (${completedItems}/${totalItems} completed)`, 'warning');
      return;
    }

    setLoading(true);
    try {
      // Prepare completion data
      const completionData = {
        encounterId,
        patientId: patient.id,
        surgicalProcedureId: surgicalProcedure.id,
        checklistStatus
      };

      // Use service to create post-op completion
      await surgicalPhasesService.createPostOpCompletion(completionData);

      showSnackbar('Post-operative phase completed successfully', 'success');
    } catch (error) {
      console.error('Error completing post-op phase:', error);
      showSnackbar('Failed to complete post-op phase', 'error');
    }
    setLoading(false);
  };

  const handleDischargePatient = async () => {
    const completedItems = Object.values(checklistStatus).filter(Boolean).length;
    const totalItems = Object.keys(checklistStatus).length;

    if (completedItems < totalItems) {
      showSnackbar(`Please complete all post-op tasks before discharge (${completedItems}/${totalItems} completed)`, 'error');
      return;
    }

    setLoading(true);
    try {
      // Prepare discharge data
      const dischargeData = {
        encounterId,
        patientId: patient.id,
        surgicalProcedureId: surgicalProcedure.id,
        dischargeStatus: 'Completed'
      };

      // Use service to create patient discharge
      await surgicalPhasesService.createPatientDischarge(dischargeData);

      showSnackbar('Patient discharged successfully', 'success');
    } catch (error) {
      console.error('Error discharging patient:', error);
      showSnackbar('Failed to discharge patient', 'error');
    }
    setLoading(false);
  };

  const handlePrintSummary = () => {
    const printContent = `
      SURGICAL POST-OPERATIVE SUMMARY
      ================================
      
      Patient: ${patient.name}
      MRN: ${patient.mrn}
      Procedure: ${surgicalProcedure.procedureType}
      Date: ${new Date().toLocaleDateString()}
      
      POST-OP CHECKLIST STATUS:
      • Post-Op Orders: ${checklistStatus.postOpOrders ? '✓ Complete' : '✗ Pending'}
      • Discharge Instructions: ${checklistStatus.dischargeInstructions ? '✓ Complete' : '✗ Pending'}
      • Follow-Up Scheduled: ${checklistStatus.followUpScheduled ? '✓ Complete' : '✗ Pending'}
      • Prescriptions: ${checklistStatus.prescriptions ? '✓ Complete' : '✗ Pending'}
      • Home Care Planning: ${checklistStatus.homeCarePlanning ? '✓ Complete' : '✗ Pending'}
      • Patient Education: ${checklistStatus.patientEducation ? '✓ Complete' : '✗ Pending'}
      
      Printed by: ${user?.name}
      Print Date: ${new Date().toLocaleString()}
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`<pre>${printContent}</pre>`);
      printWindow.document.close();
      printWindow.print();
      showSnackbar('Summary printed successfully', 'success');
    }
  };

  const checklist = [
    {
      key: 'postOpOrders',
      title: 'Post-Op Orders',
      action: (
        <Button 
          variant="outlined" 
          color="primary" 
          size="small"
          onClick={handleReviewOrders}
          disabled={loading}
        >
          Review Orders
        </Button>
      ),
      checked: checklistStatus.postOpOrders
    },
    {
      key: 'dischargeInstructions',
      title: 'Discharge Instructions',
      action: (
        <Button 
          variant="outlined" 
          color="primary" 
          size="small"
          onClick={handleGenerateInstructions}
          disabled={loading}
        >
          Generate Instructions
        </Button>
      ),
      checked: checklistStatus.dischargeInstructions
    },
    {
      key: 'followUpScheduled',
      title: 'Follow-Up Appointment',
      action: (
        <Button 
          variant="outlined" 
          color="primary" 
          size="small"
          onClick={handleScheduleFollowUp}
          disabled={loading}
        >
          Schedule Follow-Up
        </Button>
      ),
      checked: checklistStatus.followUpScheduled
    },
    {
      key: 'prescriptions',
      title: 'Prescriptions',
      action: (
        <Button 
          variant="outlined" 
          color="primary" 
          size="small"
          onClick={handleWritePrescriptions}
          disabled={loading}
        >
          Write Prescriptions
        </Button>
      ),
      checked: checklistStatus.prescriptions
    },
    {
      key: 'homeCarePlanning',
      title: 'Home Care Planning',
      action: (
        <Button 
          variant="outlined" 
          color="primary" 
          size="small"
          onClick={handleDocumentPlan}
          disabled={loading}
        >
          Document Plan
        </Button>
      ),
      checked: checklistStatus.homeCarePlanning
    },
    {
      key: 'patientEducation',
      title: 'Patient Education',
      action: (
        <Button 
          variant="outlined" 
          color="primary" 
          size="small"
          onClick={handleProvideEducation}
          disabled={loading}
        >
          Provide Education
        </Button>
      ),
      checked: checklistStatus.patientEducation
    }
  ];

  return (
    <Box sx={{ p: 0 }}>
      {/* Status Bar */}
      <Card sx={{ mb: 3, borderRadius: 2, background: '#f0fff0' }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Post-Op Status:
            </Typography>
            <Chip
              label={surgicalProcedure.status === 'completed' ? 'Completed' : 'In Progress'}
              color={surgicalProcedure.status === 'completed' ? 'success' : 'warning'}
              size="small"
              sx={{ ml: 1, fontWeight: 600, fontSize: 14 }}
              icon={<AssignmentTurnedInIcon sx={{ fontSize: 18 }} />}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Discharge Planning
          </Typography>
        </CardContent>
      </Card>

      {/* Procedure Summary */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Procedure Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Procedure Type</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {surgicalProcedure.procedureType}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Specialty</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {surgicalProcedure.specialty}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Primary Surgeon</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {surgicalProcedure.orderingProvider || 'Not specified'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Procedure Status</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {surgicalProcedure.status}
              </Typography>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Operative Summary
          </Typography>
          <Typography variant="body2" sx={{ 
            p: 2, 
            backgroundColor: '#f5f5f5', 
            borderRadius: 1,
            fontStyle: 'italic',
            color: 'text.secondary'
          }}>
            Operative summary will be documented by the surgeon post-procedure.
          </Typography>
        </CardContent>
      </Card>

      {/* Post-Op Checklist */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Post-Op Checklist
      </Typography>
      <Stack spacing={2}>
        {checklist.map(item => (
          <Card key={item.key} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {item.checked ? (
                  <CheckCircleIcon sx={{ color: 'success.main', mr: 2 }} />
                ) : (
                  <CancelIcon sx={{ color: 'error.main', mr: 2 }} />
                )}
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {item.title}
                </Typography>
              </Box>
              <Box>
                {item.action}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Sticky Footer Actions */}
      <Box sx={{
        position: 'sticky',
        bottom: 0,
        left: 0,
        width: '100%',
        background: '#f0fff0',
        borderTop: '1px solid #e0e0e0',
        mt: 4,
        py: 2,
        zIndex: 10
      }}>
        <Stack direction="row" spacing={2} justifyContent="flex-start">
          <Button 
            variant="contained" 
            color="success" 
            sx={{ minWidth: 180 }}
            onClick={handleCompletePostOp}
            disabled={loading}
          >
            Complete Post-Op
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            sx={{ minWidth: 180 }}
            onClick={handleDischargePatient}
            disabled={loading}
          >
            Discharge Patient
          </Button>
          <Button 
            variant="contained" 
            color="inherit" 
            sx={{ minWidth: 140 }}
            onClick={handlePrintSummary}
            startIcon={<PrintIcon />}
          >
            Print Summary
          </Button>
        </Stack>
      </Box>

      {/* Post-Op Orders Dialog */}
      <Dialog open={openDialog === 'postOpOrders'} onClose={() => setOpenDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Post-Op Orders</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Order Type</InputLabel>
                <Select
                  value={orderForm.type}
                  label="Order Type"
                  onChange={(e) => setOrderForm(prev => ({ ...prev, type: e.target.value }))}
                >
                  <MenuItem value="Medication">Medication</MenuItem>
                  <MenuItem value="Activity">Activity</MenuItem>
                  <MenuItem value="Diet">Diet</MenuItem>
                  <MenuItem value="Therapy">Therapy</MenuItem>
                  <MenuItem value="Monitoring">Monitoring</MenuItem>
                  <MenuItem value="Lab">Lab Work</MenuItem>
                  <MenuItem value="Imaging">Imaging</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Description"
                value={orderForm.description}
                onChange={(e) => setOrderForm(prev => ({ ...prev, description: e.target.value }))}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Dosage"
                value={orderForm.dosage}
                onChange={(e) => setOrderForm(prev => ({ ...prev, dosage: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Frequency"
                value={orderForm.frequency}
                onChange={(e) => setOrderForm(prev => ({ ...prev, frequency: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Duration"
                value={orderForm.duration}
                onChange={(e) => setOrderForm(prev => ({ ...prev, duration: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleAddOrder}
                startIcon={<AddIcon />}
                disabled={loading}
              >
                Add Order
              </Button>
            </Grid>
          </Grid>

          {postOpOrders.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Current Orders</Typography>
              <List>
                {postOpOrders.map((order) => (
                  <ListItem key={order.id} divider>
                    <ListItemText
                      primary={`${order.type}: ${order.description}`}
                      secondary={`${order.dosage ? `Dosage: ${order.dosage}` : ''} ${order.frequency ? `Frequency: ${order.frequency}` : ''} ${order.duration ? `Duration: ${order.duration}` : ''}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => setPostOpOrders(prev => prev.filter(o => o.id !== order.id))}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)}>Cancel</Button>
          <Button onClick={handleCompleteOrders} variant="contained" disabled={loading}>
            Complete Orders
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discharge Instructions Dialog */}
      <Dialog open={openDialog === 'dischargeInstructions'} onClose={() => setOpenDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Discharge Instructions</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={instructionForm.category}
                  label="Category"
                  onChange={(e) => setInstructionForm(prev => ({ ...prev, category: e.target.value }))}
                >
                  <MenuItem value="Wound Care">Wound Care</MenuItem>
                  <MenuItem value="Activity">Activity</MenuItem>
                  <MenuItem value="Diet">Diet</MenuItem>
                  <MenuItem value="Medications">Medications</MenuItem>
                  <MenuItem value="Signs to Watch">Signs to Watch</MenuItem>
                  <MenuItem value="Follow-up">Follow-up</MenuItem>
                  <MenuItem value="General Care">General Care</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={instructionForm.priority}
                  label="Priority"
                  onChange={(e) => setInstructionForm(prev => ({ ...prev, priority: e.target.value as 'High' | 'Medium' | 'Low' }))}
                >
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="Low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Instruction"
                value={instructionForm.instruction}
                onChange={(e) => setInstructionForm(prev => ({ ...prev, instruction: e.target.value }))}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleAddInstruction}
                startIcon={<AddIcon />}
              >
                Add Instruction
              </Button>
            </Grid>
          </Grid>

          {dischargeInstructions.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Current Instructions</Typography>
              <List>
                {dischargeInstructions.map((instruction, index) => (
                  <ListItem key={index} divider>
                    <ListItemText
                      primary={`${instruction.category}`}
                      secondary={instruction.instruction}
                    />
                    <Chip
                      label={instruction.priority}
                      color={instruction.priority === 'High' ? 'error' : instruction.priority === 'Medium' ? 'warning' : 'default'}
                      size="small"
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => setDischargeInstructions(prev => prev.filter((_, i) => i !== index))}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)}>Cancel</Button>
          <Button onClick={handleCompleteInstructions} variant="contained" disabled={loading}>
            Complete Instructions
          </Button>
        </DialogActions>
      </Dialog>

      {/* Follow-Up Appointment Dialog */}
      <Dialog open={openDialog === 'followUp'} onClose={() => setOpenDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <EventIcon sx={{ mr: 1 }} />
          Schedule Follow-Up Appointment
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Appointment Type</InputLabel>
                <Select
                  value={followUpForm.appointmentType}
                  label="Appointment Type"
                  onChange={(e) => setFollowUpForm(prev => ({ ...prev, appointmentType: e.target.value }))}
                >
                  <MenuItem value="Post-Op Check">Post-Op Check</MenuItem>
                  <MenuItem value="Suture Removal">Suture Removal</MenuItem>
                  <MenuItem value="Wound Check">Wound Check</MenuItem>
                  <MenuItem value="Consultation">Consultation</MenuItem>
                  <MenuItem value="Therapy">Therapy</MenuItem>
                  <MenuItem value="Imaging">Imaging</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Provider"
                value={followUpForm.provider}
                onChange={(e) => setFollowUpForm(prev => ({ ...prev, provider: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Timeframe</InputLabel>
                <Select
                  value={followUpForm.timeframe}
                  label="Timeframe"
                  onChange={(e) => setFollowUpForm(prev => ({ ...prev, timeframe: e.target.value }))}
                >
                  <MenuItem value="1-2 weeks">1-2 weeks</MenuItem>
                  <MenuItem value="2-3 weeks">2-3 weeks</MenuItem>
                  <MenuItem value="4-6 weeks">4-6 weeks</MenuItem>
                  <MenuItem value="3 months">3 months</MenuItem>
                  <MenuItem value="6 months">6 months</MenuItem>
                  <MenuItem value="As needed">As needed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={followUpForm.notes}
                onChange={(e) => setFollowUpForm(prev => ({ ...prev, notes: e.target.value }))}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)}>Cancel</Button>
          <Button onClick={handleCompleteFollowUp} variant="contained" disabled={loading}>
            Schedule Appointment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Prescriptions Dialog */}
      <Dialog open={openDialog === 'prescriptions'} onClose={() => setOpenDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <LocalPharmacyIcon sx={{ mr: 1 }} />
          Write Prescriptions
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Medication"
                value={prescriptionForm.medication}
                onChange={(e) => setPrescriptionForm(prev => ({ ...prev, medication: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Dosage"
                value={prescriptionForm.dosage}
                onChange={(e) => setPrescriptionForm(prev => ({ ...prev, dosage: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Quantity"
                value={prescriptionForm.quantity}
                onChange={(e) => setPrescriptionForm(prev => ({ ...prev, quantity: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Frequency"
                value={prescriptionForm.frequency}
                onChange={(e) => setPrescriptionForm(prev => ({ ...prev, frequency: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Duration"
                value={prescriptionForm.duration}
                onChange={(e) => setPrescriptionForm(prev => ({ ...prev, duration: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Refills"
                type="number"
                value={prescriptionForm.refills}
                onChange={(e) => setPrescriptionForm(prev => ({ ...prev, refills: parseInt(e.target.value) || 0 }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Instructions"
                value={prescriptionForm.instructions}
                onChange={(e) => setPrescriptionForm(prev => ({ ...prev, instructions: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleAddPrescription}
                startIcon={<AddIcon />}
              >
                Add Prescription
              </Button>
            </Grid>
          </Grid>

          {prescriptions.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Current Prescriptions</Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Medication</TableCell>
                      <TableCell>Dosage</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Frequency</TableCell>
                      <TableCell>Refills</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {prescriptions.map((prescription) => (
                      <TableRow key={prescription.id}>
                        <TableCell>{prescription.medication}</TableCell>
                        <TableCell>{prescription.dosage}</TableCell>
                        <TableCell>{prescription.quantity}</TableCell>
                        <TableCell>{prescription.frequency}</TableCell>
                        <TableCell>{prescription.refills}</TableCell>
                        <TableCell>
                          <IconButton onClick={() => setPrescriptions(prev => prev.filter(p => p.id !== prescription.id))}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)}>Cancel</Button>
          <Button onClick={handleCompletePrescriptions} variant="contained" disabled={loading}>
            Complete Prescriptions
          </Button>
        </DialogActions>
      </Dialog>

      {/* Home Care Planning Dialog */}
      <Dialog open={openDialog === 'homeCarePlanning'} onClose={() => setOpenDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <HomeIcon sx={{ mr: 1 }} />
          Home Care Planning
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={homeCareForm.category}
                  label="Category"
                  onChange={(e) => setHomeCareForm(prev => ({ ...prev, category: e.target.value }))}
                >
                  <MenuItem value="Wound Care">Wound Care</MenuItem>
                  <MenuItem value="Medication Management">Medication Management</MenuItem>
                  <MenuItem value="Physical Therapy">Physical Therapy</MenuItem>
                  <MenuItem value="Occupational Therapy">Occupational Therapy</MenuItem>
                  <MenuItem value="Nursing Care">Nursing Care</MenuItem>
                  <MenuItem value="Equipment">Equipment</MenuItem>
                  <MenuItem value="Assistance">Assistance</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Description"
                value={homeCareForm.description}
                onChange={(e) => setHomeCareForm(prev => ({ ...prev, description: e.target.value }))}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Frequency"
                value={homeCareForm.frequency}
                onChange={(e) => setHomeCareForm(prev => ({ ...prev, frequency: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Duration"
                value={homeCareForm.duration}
                onChange={(e) => setHomeCareForm(prev => ({ ...prev, duration: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleAddHomeCareItem}
                startIcon={<AddIcon />}
              >
                Add Home Care Item
              </Button>
            </Grid>
          </Grid>

          {homeCareItems.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Home Care Plan</Typography>
              <List>
                {homeCareItems.map((item, index) => (
                  <ListItem key={index} divider>
                    <ListItemText
                      primary={`${item.category}: ${item.description}`}
                      secondary={`Frequency: ${item.frequency} | Duration: ${item.duration}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => setHomeCareItems(prev => prev.filter((_, i) => i !== index))}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)}>Cancel</Button>
          <Button onClick={handleCompleteHomeCare} variant="contained" disabled={loading}>
            Complete Home Care Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Patient Education Dialog */}
      <Dialog open={openDialog === 'patientEducation'} onClose={() => setOpenDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <SchoolIcon sx={{ mr: 1 }} />
          Patient Education
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={[
                  'Wound Care Instructions',
                  'Medication Instructions',
                  'Activity Restrictions',
                  'Diet Instructions',
                  'Sign and Symptoms to Watch',
                  'Follow-up Care',
                  'Pain Management',
                  'Infection Prevention',
                  'Physical Therapy Exercises',
                  'Recovery Timeline'
                ]}
                value={educationForm.topic}
                onChange={(_, newValue) => setEducationForm(prev => ({ ...prev, topic: newValue || '' }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Education Topic"
                    fullWidth
                  />
                )}
                freeSolo
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleAddEducationTopic}
                startIcon={<AddIcon />}
              >
                Add Education Topic
              </Button>
            </Grid>
          </Grid>

          {educationTopics.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Education Provided</Typography>
              <List>
                {educationTopics.map((topic, index) => (
                  <ListItem key={index} divider>
                    <ListItemText
                      primary={topic.topic}
                      secondary={`Provided by: ${topic.provider}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => setEducationTopics(prev => prev.filter((_, i) => i !== index))}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)}>Cancel</Button>
          <Button onClick={handleCompleteEducation} variant="contained" disabled={loading}>
            Complete Education
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SurgicalPostOpPhase; 